import classNames from 'classnames';
import { Button, Wrapper } from 'components';

import { Table, Avatar, Tooltip } from 'components-antd';
import { Icons as Icon } from '../../Icons/Icons';

import styles from './styles.module.scss';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Task } from 'pages/Workshop/Transactions/TransactionTasks/components';
import {
  getSymphonyActiveClients,
  getSymphonyBuyerClients,
  getSymphonyNonRetentionClients,
  getSymphonyPendingClients,
  getSymphonyRetentionClients,
  getSymphonySellerClients,
} from 'api/symphony';
import { showErrorMessage, showSuccessMessage } from 'helpers';
import { setClientsListSendDateAction } from 'store/actions/clientsList';
import { postReinviteClientById } from 'api/clientsList';
import { useDispatch } from 'react-redux';
import moment from 'moment';

export const ClientsModal = ({ row, variant = 'active' }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [isPending, setIsPending] = useState(true);
  const [selectedTooltip, setSelectedTooltip] = useState(null);

  const fetchClientsData = async () => {
    const variantApiMap = {
      active: getSymphonyActiveClients,
      pendingClients: getSymphonyPendingClients,
      buyers: getSymphonyBuyerClients,
      sellers: getSymphonySellerClients,
      retention: getSymphonyRetentionClients,
      nonRetention: getSymphonyNonRetentionClients,
    };
    const selectedApi = variantApiMap[variant];
    try {
      const { data } = await selectedApi({ agentId: row?.Id });
      if (data?.length) {
        setData(data);
      }
    } finally {
      setIsPending(false);
    }
  };

  useEffect(() => {
    fetchClientsData();
  }, []);

  const handleCopy = (InviteLink, id) => {
    navigator.clipboard.writeText(InviteLink);
    setSelectedTooltip(id);
    setTimeout(() => setSelectedTooltip(null), 3000);
  };

  const resendInvite = async ({ Id, SendInvitation }) => {
    try {
      await postReinviteClientById({ id: Id });
      dispatch(setClientsListSendDateAction(Id));
      showSuccessMessage(`Client ${SendInvitation ? 'reinvited' : 'invited'}.`);
    } catch (err) {
      showErrorMessage(`Unable to ${SendInvitation ? 'reinvite' : 'invite'} client.`);
    }
  };

  const detailColumns = [
    {
      title: 'Client',
      key: 'ClientName',
      dataIndex: 'ClientName',
      sorter: (a, b) => {
        let name = a?.ClientName;
        let secondName = b?.ClientName;
        return name.localeCompare(secondName);
      },
      render: (ClientName) => (
        <div className={classNames(styles.val, styles.client)}>
          <Avatar className={styles.avatar} size={32} src={''}>
            {ClientName.split(' ')
              .map((name) => name.charAt(0).toUpperCase())
              .join('')}
          </Avatar>
          <p className={styles.name}>{ClientName}</p>
        </div>
      ),
    },
    {
      title: 'Tags',
      key: 'Tags',
      dataIndex: 'Tags',
      sorter: (a, b) => {
        if (a.Status < b.Status) return -1;
        if (a.Status > b.Status) return 1;
        return 0;
      },
      render: (Tags) => {
        const filteredTags = Tags?.filter(Boolean);
        return (
          <div className={styles.val}>{filteredTags?.length ? filteredTags?.join(', ') : '-'}</div>
        );
      },
    },
    ...(variant === 'pendingClients'
      ? [
          {
            title: 'Status',
            key: 'InviteSentAt',
            dataIndex: 'InviteSentAt',
            sorter: (a, b) => {
              const dateA = new Date(a.InviteSentAt).getTime();
              const dateB = new Date(b.InviteSentAt).getTime();
              return dateA - dateB;
            },
            render: (InviteSentAt, { SendInvitation }) => {
              const formatted = SendInvitation
                ? `Sent ${moment(InviteSentAt).format('M/D/YYYY')}`
                : 'Pending Email Invite';
              return <div className={styles.val}>{formatted}</div>;
            },
          },
        ]
      : []),
    {
      title: '',
      key: 'Id',
      dataIndex: 'Id',
      render: (_, { Id, ClientName, InviteLink, SendInvitation }) => {
        return (
          <div className={classNames(styles.val, styles.buttons)}>
            {variant === 'pendingClients' ? (
              <>
                {
                  <span
                    className={classNames(styles.tooltip, {
                      [styles.show]: selectedTooltip === Id,
                    })}
                  >
                    {`Invitation link copied.`}
                  </span>
                }
                <Tooltip
                  placement="top"
                  title={'Copy Invite Link'}
                  overlayClassName={classNames(styles.tooltipSend, 'mosaikTooltip')}
                >
                  <Button
                    className={classNames(styles.button, styles.iconButton)}
                    onClick={() => {
                      handleCopy(InviteLink, Id);
                    }}
                    iconLeft={<Icon className={styles.icon} variant={Icon.COPY} />}
                  />
                </Tooltip>
                <Tooltip
                  placement="top"
                  title={`${SendInvitation ? 'Resend Invite' : 'Send Invite'}`}
                  overlayClassName={classNames(styles.tooltipSend, 'mosaikTooltip')}
                >
                  <Button
                    isPending={false}
                    className={classNames(styles.button, styles.iconButton)}
                    onClick={() => {
                      resendInvite({ Id, SendInvitation });
                    }}
                    iconLeft={
                      <Icon
                        className={styles.icon}
                        variant={SendInvitation ? Icon.RESEND : Icon.SEND}
                      />
                    }
                  />
                </Tooltip>
              </>
            ) : (
              <Button
                isPending={false}
                className={styles.button}
                onClick={() => {
                  history.push(`/profile/client/${Id}`);
                }}
                title={'View'}
                disabled={false}
              />
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Wrapper isPending={isPending}>
      <Task />
      <Table
        loading={false}
        className={styles.detailsTable}
        bordered
        columns={detailColumns}
        dataSource={data}
        pagination={false}
        showSorterTooltip={false}
      />
    </Wrapper>
  );
};
